/**
 * PVDesignWorkspace - Complete PV System Design Workspace
 * 
 * Integrates all design components:
 * - Technical Configurator (component selection)
 * - String Configuration (electrical compatibility)
 * - Roof Layout Editor (planimetry and production)
 * 
 * Replaces the simple configurator in "Progettazione e Fattibilità" step.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Separator } from './ui/separator';
import { toast } from 'sonner';
import api from '../lib/api';
import TechnicalConfiguratorV2 from './TechnicalConfiguratorV2';
import StringConfigurator from './StringConfigurator';
import RoofLayoutEditor from './RoofLayoutEditor';
import InventoryContractIntegration from './InventoryContractIntegration';
import {
  Sun,
  Zap,
  Cable,
  MapPin,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Loader2,
  Settings2,
  FileText,
  Download,
  Save,
  Gauge,
  Euro,
  Battery,
  Package
} from 'lucide-react';

// Status colors
const STATUS_CONFIG = {
  valid: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', icon: CheckCircle2 },
  warning: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', icon: AlertTriangle },
  invalid: { bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-700', icon: XCircle }
};

// Technical Summary Component
function TechnicalSummary({ bomData, stringDesign, productionData }) {
  const calculations = bomData?.calculations || {};
  
  return (
    <Card className="bg-gradient-to-br from-zinc-900 to-zinc-800 text-white border-0 rounded-2xl shadow-lg">
      <CardContent className="py-5">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          <div className="text-center">
            <Sun className="h-5 w-5 mx-auto mb-1 text-amber-400" />
            <div className="text-xl font-mono font-bold">{calculations.totalModuliPower || '0.00'}</div>
            <div className="text-xs text-zinc-400">kWp</div>
          </div>
          <div className="text-center">
            <Zap className="h-5 w-5 mx-auto mb-1 text-blue-400" />
            <div className="text-xl font-mono font-bold">{calculations.totalInverterPower || '0.00'}</div>
            <div className="text-xs text-zinc-400">kW Inverter</div>
          </div>
          <div className="text-center">
            <Gauge className="h-5 w-5 mx-auto mb-1 text-indigo-400" />
            <div className="text-xl font-mono font-bold">{calculations.dcAcRatio || '-'}</div>
            <div className="text-xs text-zinc-400">DC/AC</div>
          </div>
          <div className="text-center">
            <Cable className="h-5 w-5 mx-auto mb-1 text-purple-400" />
            <div className="text-xl font-mono font-bold">{stringDesign?.num_strings || '-'}</div>
            <div className="text-xs text-zinc-400">Stringhe</div>
          </div>
          <div className="text-center">
            <Battery className="h-5 w-5 mx-auto mb-1 text-emerald-400" />
            <div className="text-xl font-mono font-bold">{calculations.totalStorageCapacity || '0.00'}</div>
            <div className="text-xs text-zinc-400">kWh Storage</div>
          </div>
          <div className="text-center">
            <Sun className="h-5 w-5 mx-auto mb-1 text-orange-400" />
            <div className="text-xl font-mono font-bold">
              {productionData?.annual_production?.energy_kwh 
                ? (productionData.annual_production.energy_kwh / 1000).toFixed(1) 
                : '-'}
            </div>
            <div className="text-xs text-zinc-400">MWh/anno</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Compatibility Status Component
function CompatibilityStatus({ validation, stringDesign }) {
  const checks = [
    {
      label: 'Componenti selezionati',
      status: validation?.moduliCount > 0 && validation?.inverterCount > 0 ? 'ok' : 'error',
      detail: `${validation?.moduliCount || 0} moduli, ${validation?.inverterCount || 0} inverter`
    },
    {
      label: 'Compatibilità moduli/inverter',
      status: stringDesign?.status === 'compatibile' ? 'ok' : 
              stringDesign?.status === 'da_verificare' ? 'warning' : 'error',
      detail: stringDesign?.status || 'Non calcolato'
    },
    {
      label: 'Rapporto DC/AC',
      status: validation?.dcAcStatus?.status === 'ok' ? 'ok' :
              validation?.dcAcStatus?.status === 'warning' ? 'warning' : 'neutral',
      detail: validation?.dcAcStatus?.message || 'Non calcolabile'
    },
    {
      label: 'Stringhe configurate',
      status: stringDesign?.num_strings > 0 ? 'ok' : 'neutral',
      detail: stringDesign?.num_strings ? `${stringDesign.num_strings} stringhe da ${stringDesign.modules_per_string} moduli` : 'Non configurate'
    }
  ];

  return (
    <Card className="bg-white border-zinc-200 rounded-2xl shadow-sm h-full">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-bold text-zinc-900 flex items-center gap-2">
          <CheckCircle2 className="h-5 w-5 text-zinc-500" />
          Stato Compatibilità
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-0">
        {checks.map((check, idx) => (
          <div key={`check-${check.label}-${idx}`} className="flex items-center justify-between py-2 border-b border-zinc-50 last:border-0">
            <span className="text-sm text-zinc-700">{check.label}</span>
            <div className="flex items-center gap-2">
              <span className="text-xs text-zinc-500">{check.detail}</span>
              {check.status === 'ok' ? (
                <CheckCircle2 className="h-4 w-4 text-emerald-500" />
              ) : check.status === 'warning' ? (
                <AlertTriangle className="h-4 w-4 text-amber-500" />
              ) : check.status === 'error' ? (
                <XCircle className="h-4 w-4 text-rose-500" />
              ) : (
                <span className="text-zinc-300">-</span>
              )}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

// Warnings Panel
function WarningsPanel({ validation, stringDesign }) {
  const allWarnings = [
    ...(validation?.errors || []).map(e => ({ ...e, severity: 'error' })),
    ...(validation?.warnings || []).map(w => ({ ...w, severity: 'warning' })),
    ...(stringDesign?.warnings || [])
  ];

  if (allWarnings.length === 0) {
    return (
      <div className="flex items-center gap-2 p-4 rounded-xl bg-emerald-50 border border-emerald-200">
        <CheckCircle2 className="h-5 w-5 text-emerald-500" />
        <span className="text-emerald-700 font-medium">Configurazione valida - Nessun problema rilevato</span>
      </div>
    );
  }

  const hasErrors = allWarnings.some(w => w.severity === 'error');
  const statusConfig = hasErrors ? STATUS_CONFIG.invalid : STATUS_CONFIG.warning;
  const StatusIcon = statusConfig.icon;

  return (
    <div className={`p-4 rounded-xl border ${statusConfig.bg} ${statusConfig.border}`}>
      <div className="flex items-center gap-2 mb-3">
        <StatusIcon className={`h-5 w-5 ${statusConfig.text}`} />
        <span className={`font-semibold ${statusConfig.text}`}>
          {hasErrors ? 'Configurazione non valida' : 'Configurazione da verificare'}
        </span>
      </div>
      <ul className="space-y-1">
        {allWarnings.map((w, idx) => (
          <li key={`warn-${w.message?.substring(0, 15) || idx}-${idx}`} className={`text-sm ${statusConfig.text} flex items-start gap-2`}>
            <span className="mt-0.5">•</span>
            <span>{w.message}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Main Component
export default function PVDesignWorkspace({
  contractId,
  nodeId,
  readOnly = false,
  onConfigChange,
  onValidationChange,
  contractAddress
}) {
  // State
  const [activeTab, setActiveTab] = useState('configurator');
  const [bomData, setBomData] = useState(null);
  const [stringDesign, setStringDesign] = useState(null);
  const [layoutData, setLayoutData] = useState(null);
  const [productionData, setProductionData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Extract module and inverter specs from BOM
  const { moduleSpecs, inverterSpecs, totalModules, totalPowerKwp } = useMemo(() => {
    if (!bomData?.bom?.items) {
      return { moduleSpecs: null, inverterSpecs: null, totalModules: 0, totalPowerKwp: 0 };
    }

    let modSpecs = null;
    let invSpecs = null;
    let modCount = 0;
    let totalKwp = 0;

    bomData.bom.items.forEach(item => {
      const catalogItem = item.catalog_item;
      if (!catalogItem) return;

      if (catalogItem.type_code === 'MODULI') {
        modSpecs = catalogItem.technical_specs;
        modCount += item.quantity;
        if (modSpecs?.pmax_w) {
          totalKwp += (modSpecs.pmax_w * item.quantity) / 1000;
        }
      }

      if (catalogItem.type_code === 'INVERTER' || catalogItem.type_code === 'INVERTER_IBRIDO') {
        invSpecs = catalogItem.technical_specs;
      }
    });

    return {
      moduleSpecs: modSpecs,
      inverterSpecs: invSpecs,
      totalModules: modCount,
      totalPowerKwp: totalKwp
    };
  }, [bomData]);

  // Handle config change from TechnicalConfigurator
  const handleConfigChange = useCallback((data) => {
    setBomData(data);
    if (onConfigChange) {
      onConfigChange(data);
    }
  }, [onConfigChange]);

  // Handle validation change from TechnicalConfigurator
  const handleValidationChange = useCallback((validation) => {
    if (onValidationChange) {
      // Combine with string design validation
      const combinedValidation = {
        ...validation,
        stringDesign: stringDesign
      };
      onValidationChange(combinedValidation);
    }
  }, [onValidationChange, stringDesign]);

  // Handle string design change
  const handleStringDesignChange = useCallback((design) => {
    setStringDesign(design);
  }, []);

  // Handle layout change
  const handleLayoutChange = useCallback((layout) => {
    setLayoutData(layout);
  }, []);

  // Save design to contract node
  const saveDesign = async () => {
    if (!contractId || !nodeId) return;

    setSaving(true);
    try {
      // Save design data to node_data
      await api.put(`/contract-nodes/${nodeId}/form-data`, {
        form_data: {
          pv_design: {
            string_design: stringDesign,
            layout_data: layoutData,
            production_estimate: productionData,
            total_power_kwp: totalPowerKwp,
            updated_at: new Date().toISOString()
          }
        }
      });
      toast.success('Progetto salvato');
    } catch (error) {
      toast.error('Errore nel salvataggio');
      console.error('Save error:', error);
    } finally {
      setSaving(false);
    }
  };

  // Generate and download PDF
  const [generatingPdf, setGeneratingPdf] = useState(false);
  
  const generatePDF = async () => {
    setGeneratingPdf(true);
    try {
      const pdfData = {
        contract_number: bomData?.contract_number || contractId,
        customer_name: bomData?.customer_name || '',
        total_modules: totalModules,
        total_dc_power_kwp: totalPowerKwp,
        inverter_power_kw: bomData?.calculations?.totalInverterPower || 0,
        dc_ac_ratio: stringDesign?.dc_ac_ratio || bomData?.calculations?.dcAcRatio || 0,
        num_strings: stringDesign?.num_strings || 0,
        modules_per_string: stringDesign?.modules_per_string || 0,
        num_mppt: stringDesign?.num_mppt || 0,
        storage_kwh: parseFloat(bomData?.calculations?.totalStorageCapacity) || 0,
        voc_string_max_v: stringDesign?.voc_string_max_v || 0,
        vmp_string_v: stringDesign?.vmp_string_v || 0,
        isc_string_a: stringDesign?.isc_string_a || 0,
        status: stringDesign?.status || 'non_compatibile',
        warnings: stringDesign?.warnings || [],
        mppt_configs: stringDesign?.mppt_configs || [],
        layout_data: layoutData,
        production_data: productionData,
        location: productionData?.location || null
      };
      
      const response = await api.post('/pv-design/generate-pdf', pdfData);
      
      if (response.data.success && response.data.pdf_base64) {
        // Convert base64 to blob and download
        const byteCharacters = atob(response.data.pdf_base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        
        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = response.data.filename || 'progetto_pv.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        toast.success('PDF generato con successo');
      } else {
        throw new Error('PDF generation failed');
      }
    } catch (error) {
      toast.error('Errore nella generazione PDF');
      console.error('PDF error:', error);
    } finally {
      setGeneratingPdf(false);
    }
  };

  // Validation summary
  const validation = bomData?.validation;
  const isValid = validation?.status === 'valid' && 
                  stringDesign?.status === 'compatibile';

  return (
    <div className="space-y-6" data-testid="pv-design-workspace">
      {/* Warnings Panel - Always visible at top */}
      <WarningsPanel validation={validation} stringDesign={stringDesign} />

      {/* Summary + Compatibility Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <TechnicalSummary 
            bomData={bomData} 
            stringDesign={stringDesign}
            productionData={productionData}
          />
        </div>
        <div className="lg:col-span-1">
          <CompatibilityStatus 
            validation={{
              moduliCount: bomData?.calculations?.moduliCount,
              inverterCount: bomData?.calculations?.inverterCount,
              dcAcStatus: bomData?.calculations?.dcAcStatus
            }}
            stringDesign={stringDesign}
          />
        </div>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="flex items-center justify-between mb-4">
          <TabsList className="bg-zinc-100 p-1 rounded-xl">
            <TabsTrigger 
              value="configurator" 
              className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm px-4 py-2"
            >
              <Settings2 className="h-4 w-4 mr-2" />
              Configuratore Impianto
            </TabsTrigger>
            <TabsTrigger 
              value="strings" 
              className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm px-4 py-2"
            >
              <Cable className="h-4 w-4 mr-2" />
              Stringhe e MPPT
              {stringDesign?.status && (
                <Badge 
                  variant="outline" 
                  className={`ml-2 text-xs ${
                    stringDesign.status === 'compatibile' ? 'bg-emerald-50 text-emerald-700' :
                    stringDesign.status === 'da_verificare' ? 'bg-amber-50 text-amber-700' :
                    'bg-rose-50 text-rose-700'
                  }`}
                >
                  {stringDesign.status === 'compatibile' ? '✓' : '!'}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger 
              value="layout" 
              className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm px-4 py-2"
            >
              <MapPin className="h-4 w-4 mr-2" />
              Layout Tetto
            </TabsTrigger>
            <TabsTrigger 
              value="inventory" 
              className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm px-4 py-2"
              data-testid="tab-inventory-pv"
            >
              <Package className="h-4 w-4 mr-2" />
              Materiali
            </TabsTrigger>
          </TabsList>

          {!readOnly && (
            <div className="flex items-center gap-2">
              <Button 
                variant="outline"
                onClick={generatePDF} 
                disabled={generatingPdf || !stringDesign}
                data-testid="generate-pdf-btn"
              >
                {generatingPdf ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
                Scarica PDF
              </Button>
              <Button 
                onClick={saveDesign} 
                disabled={saving}
                className="bg-zinc-900 hover:bg-zinc-800"
              >
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Salva Progetto
              </Button>
            </div>
          )}
        </div>

        <TabsContent value="configurator" className="mt-0">
          <TechnicalConfiguratorV2
            contractId={contractId}
            nodeId={nodeId}
            readOnly={readOnly}
            onConfigChange={handleConfigChange}
            onValidationChange={handleValidationChange}
          />
        </TabsContent>

        <TabsContent value="strings" className="mt-0">
          <StringConfigurator
            moduleSpecs={moduleSpecs}
            inverterSpecs={inverterSpecs}
            totalModules={totalModules}
            onDesignChange={handleStringDesignChange}
          />
        </TabsContent>

        <TabsContent value="layout" className="mt-0">
          <RoofLayoutEditor
            moduleSpecs={moduleSpecs}
            totalModules={totalModules}
            totalPowerKwp={totalPowerKwp}
            onLayoutChange={handleLayoutChange}
            contractAddress={contractAddress}
          />
        </TabsContent>

        <TabsContent value="inventory" className="mt-0">
          <InventoryContractIntegration
            contractId={contractId}
            contractNumber={bomData?.contract_number}
            nodeId={nodeId}
            readOnly={readOnly}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
